import React, { useState, useRef, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaRunning } from 'react-icons/fa';
import { formatDate } from '../../utils/helpers';
import { MdSportsSoccer } from 'react-icons/md'; // For matches
import { IoFootsteps } from 'react-icons/io5'; // For training
import '../../scss/calendar/calendarCarousel.scss';
import CalendarEventModal from './CalendarEventModal';
import api from '../../services/apiService';
const teamId = sessionStorage.getItem('selected_team_id');


const CalendarCarousel = ({ dates, selectedDate, onDateSelect, events, hasTraining = true }) => {
    const scrollRef = useRef(null);
    const initialScrollDone = useRef(false);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(
        new Date().toLocaleString('default', { month: 'long', year: 'numeric' })
    );
    const [selectedEvent, setSelectedEvent] = useState(null);
    
    // Use events directly from props - removing duplicate fetch
    const allEvents = events;

    useEffect(() => {
        if (!initialScrollDone.current && dates.length > 0 && scrollRef.current) {
            const today = new Date().toISOString().split('T')[0];
            // Find index of today by matching just the date part
            const todayIndex = dates.findIndex(date => {
                const datePart = date.split('-').slice(0, 3).join('-');
                return datePart === today;
            });
            
            if (todayIndex !== -1) {
                const todayElement = scrollRef.current.children[todayIndex];
                const containerWidth = scrollRef.current.clientWidth;
                const todayOffset = todayElement.offsetLeft - (containerWidth / 2) + (todayElement.clientWidth / 2);
                scrollRef.current.scrollTo({ left: todayOffset, behavior: 'smooth' });
                initialScrollDone.current = true;
            }
        }
    }, [dates]);

    const getVisibleMonth = () => {
        const container = scrollRef.current;
        if (!container) return null;

        const containerLeft = container.scrollLeft;
        const containerWidth = container.clientWidth;
        const centerPosition = containerLeft + (containerWidth / 2);
        
        const dateItems = container.getElementsByClassName('date-item');
        for (let item of dateItems) {
            const itemLeft = item.offsetLeft;
            const itemRight = itemLeft + item.offsetWidth;
            if (itemLeft <= centerPosition && itemRight >= centerPosition) {
                const datePart = item.getAttribute('data-date');
                return new Date(datePart)
                    .toLocaleString('default', { month: 'long', year: 'numeric' });
            }
        }
        return null;
    };

    const handleDateClick = (date) => {
        // Extract just the date part from the unique identifier
        const datePart = date.split('-').slice(0, 3).join('-');
        
        // Only proceed if the clicked date has an event
        const eventType = getEventType(date);
        if (!eventType) return;

        onDateSelect(date);
        
        // Update month based on clicked date
        const clickedMonth = new Date(datePart).toLocaleString('default', { month: 'long', year: 'numeric' });
        setCurrentMonth(clickedMonth);
        
        const event = allEvents?.find(event => {
            const eventDate = new Date(event.match_date).toISOString().split('T')[0];
            return eventDate === datePart;
        });

        if (event) {
            setSelectedEvent(event);
        }
    };

    const handleScroll = () => {
        const container = scrollRef.current;
        if (container) {
            // Handle arrow visibility
            setShowLeftArrow(container.scrollLeft > 0);
            setShowRightArrow(
                container.scrollLeft < (container.scrollWidth - container.clientWidth - 10)
            );

            // Update month based on visible dates
            const visibleMonth = getVisibleMonth();
            if (visibleMonth) {
                setCurrentMonth(visibleMonth);
            }
        }
    };

    const scroll = (direction) => {
        const container = scrollRef.current;
        const scrollAmount = 100;
        
        if (container) {
            const newScrollLeft = direction === 'left' 
                ? container.scrollLeft - scrollAmount 
                : container.scrollLeft + scrollAmount;
            
            container.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth'
            });
        }
    };

    const getDayName = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    const getEventType = (date) => {
        if (!allEvents) return null;
        
        const datePart = date.split('-').slice(0, 3).join('-');
        
        const event = allEvents.find(event => {
            const eventDate = new Date(event.match_date).toISOString().split('T')[0];
            return eventDate === datePart;
        });

        const eventType = event ? event.type || 'match' : null;
        return eventType;
    };

    const getEventIcon = (type) => {
        if (!type) return null;
        
        switch (type) {
            case 'match':
                return <MdSportsSoccer className="event-icon" />;
            case 'training':
                return <FaRunning className="event-icon" />;
            default:
                return null;
        }
    };

    return (
        <>
            <div className="calendar-carousel">
                <div className="calendar-carousel-header">
                    <h2>{currentMonth}</h2>
                    <div className="legend">
                        <div className="legend-item match">
                            <MdSportsSoccer className="event-icon" />
                            <span>Match Day</span>
                        </div>
                        {hasTraining && (
                            <div className="legend-item training">
                                <FaRunning className="event-icon" />
                                <span>Training</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="carousel-container">
                    <div 
                        className="dates-container" 
                        ref={scrollRef}
                        onScroll={handleScroll}
                    >
                        {dates.map((date) => {
                            const datePart = date.split('-').slice(0, 3).join('-');
                            const eventType = getEventType(date);
                            const isToday = datePart === new Date().toISOString().split('T')[0];
                            const isSelected = selectedDate === date && selectedEvent;
                            const isOldDate = new Date(datePart) < new Date().setHours(0,0,0,0);
                            
                            return (
                                <div
                                    key={date}
                                    className={`date-item 
                                        ${isSelected ? 'selected' : ''} 
                                        ${eventType || ''} 
                                        ${isToday ? 'today' : ''}
                                        ${isOldDate ? 'old-date' : ''}
                                        ${eventType ? 'clickable' : ''}
                                    `}
                                    onClick={() => handleDateClick(date)}
                                    style={{ cursor: eventType ? 'pointer' : 'default' }}
                                    data-date={datePart}
                                >
                                    <div className="day-name">{getDayName(datePart)}</div>
                                    <div className="date-number">{new Date(datePart).getDate()}</div>
                                    {eventType && (
                                        <div className="event-indicator">
                                            {getEventIcon(eventType)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {selectedEvent && (
                <CalendarEventModal 
                    event={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                />
            )}
        </>
    );
};

export default CalendarCarousel; 